import { Button, Group, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { KeyboardEvent, useState } from 'react';
import { readLegalName, readPreferredName } from '@/services/patient-name';
import { PatientProfileEdit } from '@/services/patientProfile';

const NOT_ON_FILE = 'Not on file';

interface ProfileNameControlProps {
  field: 'legalName' | 'preferredName';
  label: string;
  patient: Patient | undefined;
  /** Saves through the row, which shows "Saved to profile"; absent means read-only. */
  save?: (edit: PatientProfileEdit) => Promise<boolean>;
  saving: boolean;
  /** Clears the row's "Saved to profile" / failure line when the editor reopens. */
  onStartEditing: () => void;
}

/**
 * The legal or preferred name on the PN card's profile row (BAR-2927),
 * click-to-edit. The legal name takes first-and-middle and last separately.
 */
export function ProfileNameControl({ field, label, patient, save, saving, onStartEditing }: ProfileNameControlProps) {
  const [editing, setEditing] = useState(false);
  const [given, setGiven] = useState('');
  const [family, setFamily] = useState('');
  const [error, setError] = useState<string>();

  const isLegal = field === 'legalName';
  const legal = readLegalName(patient);
  const shown = isLegal ? [legal.given, legal.family].filter(Boolean).join(' ') : readPreferredName(patient);

  function startEditing(): void {
    setGiven(isLegal ? legal.given : shown);
    setFamily(legal.family);
    setError(undefined);
    onStartEditing();
    setEditing(true);
  }

  async function submit(): Promise<void> {
    if (!save) return;
    if (isLegal && !given.trim() && !family.trim()) {
      setError('Enter a first or last name');
      return;
    }
    if (!isLegal && !given.trim()) {
      // Clearing the preferred name is not an edit; there is nothing to write.
      setEditing(false);
      return;
    }
    if (!isLegal && !legal.given && !legal.family) {
      // Alone, it would become name[0], which every reader takes as the patient's name.
      setError('Add the legal name first');
      return;
    }
    setError(undefined);
    const edit: PatientProfileEdit = isLegal
      ? { field: 'legalName', value: { given, family } }
      : { field: 'preferredName', value: given };
    if (await save(edit)) setEditing(false);
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') void submit();
    if (event.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <Group gap={4} wrap="nowrap" align="flex-start">
        <TextInput
          size="xs"
          w={isLegal ? 120 : 160}
          placeholder={isLegal ? 'First and middle' : undefined}
          aria-label={isLegal ? `${label}, first and middle` : label}
          value={given}
          error={error}
          disabled={saving}
          autoFocus
          onChange={(event) => setGiven(event.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
        {isLegal && (
          <TextInput
            size="xs"
            w={120}
            placeholder="Last"
            aria-label={`${label}, last`}
            value={family}
            disabled={saving}
            onChange={(event) => setFamily(event.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        )}
        <Button size="xs" color="green.9" loading={saving} onClick={() => void submit()}>
          Save
        </Button>
        <Button size="xs" variant="default" disabled={saving} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </Group>
    );
  }

  if (save) {
    return (
      <UnstyledButton onClick={startEditing} aria-label={`Edit ${label.toLowerCase()}`}>
        <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} td="underline dotted">
          {shown || NOT_ON_FILE}
        </Text>
      </UnstyledButton>
    );
  }

  return (
    <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} ta="right">
      {shown || NOT_ON_FILE}
    </Text>
  );
}
